import { useEffect, useState } from 'react';

import { apiFetch } from '../../../utils';
import { useModal } from '../modal';

import './index.css';

const metricOptions = [
    { value: 'level', label: 'Level' },
    { value: 'experience', label: 'Experience' },
    { value: 'minning', label: 'Minning' },
    { value: 'smithing', label: 'Smithing' },
    { value: 'woodcutting', label: 'Woodcutting' },
    { value: 'crafting', label: 'Crafting' },
    { value: 'skinning', label: 'Skinning' },
    { value: 'leatherworking', label: 'Leatherworking' },
    { value: 'tailoring', label: 'Tailoring' },
    { value: 'alchemy', label: 'Alchemy' },
    { value: 'runecrafting', label: 'Runecrafting' },
    { value: 'jewelcrafting', label: 'Jewelcrafting' },
    { value: 'enchanting', label: 'Enchanting' },
    { value: 'cooking', label: 'Cooking' },
    { value: 'fishing', label: 'Fishing' },
];

const HighscoresModal = () => {
    const { closeModal } = useModal();

    const [metric, setMetric] = useState('level');
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState([]);
    const [results, setResults] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);

    useEffect(() => {
        let isCancelled = false;

        const loadHighscores = async () => {
            setLoading(true);
            setErrors([]);

            const response = await apiFetch('/user/highscores', 'POST', {
                metric,
                page,
                pageSize,
            });

            if (isCancelled) return;

            if (!response || response.code !== 'success') {
                const err = response?.data?.msg || ['Failed to load highscores.'];
                setErrors(Array.isArray(err) ? err : ['Failed to load highscores.']);
                setResults([]);
                setTotalPages(1);
                setTotal(0);
                setLoading(false);
                return;
            }

            const data = response.data || {};
            setResults(Array.isArray(data.results) ? data.results : []);
            setTotalPages(Math.max(data.total_pages || 1, 1));
            setTotal(data.total || 0);
            setLoading(false);
        };

        loadHighscores();

        return () => {
            isCancelled = true;
        };
    }, [metric, page, pageSize]);

    const selectedOption = metricOptions.find((option) => option.value === metric);
    const metricLabel = selectedOption ? selectedOption.label : 'Level';

    return (
        <div id='highscores-modal'>
            <div id='highscores-modal-shell'>
                <div className='modal-panel-header'>
                    <h2 className='modal-panel-title'>Highscores</h2>
                    <button
                        type='button'
                        className='button modal-panel-close'
                        title='Close highscores'
                        onClick={closeModal}
                    >
                        Close
                    </button>
                </div>

                <div id='highscores-controls'>
                    <label htmlFor='highscores-metric-select'>Category</label>
                    <select
                        id='highscores-metric-select'
                        className='highscores-select'
                        value={metric}
                        onChange={(e) => {
                            setMetric(e.target.value);
                            setPage(1);
                        }}
                    >
                        {metricOptions.map((option) => (
                            <option value={option.value} key={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>

                <div id='highscores-table-wrap'>
                    {errors.map((error) => <p className='modal-error-message' key={error}>{error}</p>)}

                    {!errors.length && (
                        <table id='highscores-table'>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Name</th>
                                    <th>{metricLabel}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr>
                                        <td colSpan='3' className='highscores-empty'>Loading highscores...</td>
                                    </tr>
                                )}

                                {!loading && results.length === 0 && (
                                    <tr>
                                        <td colSpan='3' className='highscores-empty'>No highscores found.</td>
                                    </tr>
                                )}

                                {results.map((row, index) => (
                                    <tr key={`${row.id}-${index}`}>
                                        <td>{((page - 1) * pageSize) + index + 1}</td>
                                        <td>{row.name}</td>
                                        <td>{Number(row.value || 0).toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div id='highscores-pagination'>
                    <button
                        type='button'
                        className='button highscores-page-button'
                        disabled={loading || page <= 1}
                        onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    >
                        Previous
                    </button>

                    <p>
                        Page {page} / {totalPages} ({total.toLocaleString()} total)
                    </p>

                    <button
                        type='button'
                        className='button highscores-page-button'
                        disabled={loading || page >= totalPages}
                        onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

export default HighscoresModal;
